import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import type { AuthUser as User, AuthSession as Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Cast auth to bypass strict type checks from supabase-js v2.93+ type regressions
const auth = supabase.auth as any;
import { lovable } from '@/integrations/lovable/index';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isHydrated: boolean;
  authError: boolean;
  signUp: (email: string, password: string, fullName: string, referralCode?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface HydrationResult {
  session: Session | null;
  error: Error | null;
  /** True when we kept a stored session whose access token is expired and
   *  could not be renewed — the UI must not treat this as a valid login. */
  unverified?: boolean;
}

let authHydrationPromise: Promise<HydrationResult> | null = null;

function isTransientAuthError(error: { message?: string; name?: string } | null): boolean {
  return !!error && (
    error.message === 'refreshSession timeout' ||
    error.name === 'AuthRetryableFetchError' ||
    /fetch|network|timeout/i.test(error.message ?? '')
  );
}

async function attemptRefresh(timeoutMs: number): Promise<{ session: Session | null; error: Error | null }> {
  const { data, error } = await Promise.race([
    auth.refreshSession(),
    new Promise<{ data: { session: null }; error: Error }>((resolve) =>
      setTimeout(
        () => resolve({ data: { session: null }, error: new Error('refreshSession timeout') }),
        timeoutMs,
      ),
    ),
  ]) as any;
  return { session: (data?.session as Session | null) ?? null, error: (error ?? null) as Error | null };
}

function hydrateSessionOnce(): Promise<HydrationResult> {
  if (authHydrationPromise) return authHydrationPromise;

  authHydrationPromise = (async () => {
    const stored = await auth.getSession();
    const storedSession = (stored.data?.session as Session | null) ?? null;
    if (!storedSession) {
      return { session: null, error: (stored.error ?? null) as Error | null };
    }

    const expiresAtMs = (storedSession.expires_at ?? 0) * 1000;
    if (expiresAtMs > Date.now() + 5 * 60 * 1000) {
      const validated = await auth.getUser(storedSession.access_token);
      if (!validated.error && validated.data?.user) {
        return { session: storedSession, error: null };
      }
    }

    const first = await attemptRefresh(6000);
    if (first.session) return { session: first.session, error: null };

    // Transient failures (timeout/network) must never destroy the stored
    // login: the in-flight refresh may still succeed, and autoRefreshToken
    // will retry once connectivity returns. Keep the stored session so the
    // user stays signed in; only a definitive refresh-token rejection means
    // the saved login is truly dead.
    if (isTransientAuthError(first.error)) {
      // If the stored access token is still valid, the app can keep working
      // while the refresh retries in the background.
      if (expiresAtMs > Date.now() + 30 * 1000) {
        return { session: storedSession, error: first.error };
      }

      // The stored access token is EXPIRED and could not be renewed within
      // 6s (common on slow mobile networks). Try once more with a longer
      // window before deciding — returning the expired session as-is would
      // render authenticated UI whose every data request fails with 401.
      const retry = await attemptRefresh(15000);
      if (retry.session) return { session: retry.session, error: null };

      if (isTransientAuthError(retry.error)) {
        // Still no connectivity: keep the saved login on disk (the refresh
        // token may still be valid and a later app open can renew it), but
        // flag it unverified so the UI routes to sign-in instead of showing
        // a broken "signed in" dashboard full of rejected requests.
        return { session: storedSession, error: retry.error, unverified: true };
      }

      // Definitive rejection on retry — fall through to local sign-out.
    }

    try { await auth.signOut({ scope: 'local' }); } catch { /* ignore */ }
    return { session: null, error: (first.error ?? new Error('Session expired')) as Error };
  })().finally(() => {
    window.setTimeout(() => { authHydrationPromise = null; }, 1000);
  });

  return authHydrationPromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);
  const [authError, setAuthError] = useState(false);
  const listenerSetUp = useRef(false);
  // Flag to skip re-validation when signIn already populated state
  const signInJustCompleted = useRef(false);

  useEffect(() => {
    // Prevent duplicate listeners
    if (listenerSetUp.current) return;
    listenerSetUp.current = true;

    console.log('[Auth] Hydration started');

    // CRITICAL: Restore session via getSession() FIRST, then listen for changes.
    // This prevents the race condition on mobile where INITIAL_SESSION fires
    // with null before localStorage is ready.
    let subscription: { unsubscribe: () => void } | null = null;

    const initAuth = async () => {
      try {
        const { session: readySession, error, unverified } = await hydrateSessionOnce();

        if (error) console.warn('[Auth] session hydration warning:', error.message);

        if (readySession && unverified) {
          // Stored login kept on disk but its access token is expired and
          // could not be renewed right now — do NOT render authenticated UI
          // (every data request would 401). Mark authError so protected
          // routes send the user to sign-in; the saved session stays intact
          // so a later app open can still renew it.
          console.warn('[Auth] Stored session expired and unrenewable right now; routing to sign-in');
          setSession(null);
          setUser(null);
          setAuthError(true);
        } else if (readySession) {
          console.log('[Auth] Session hydrated');
          setSession(readySession);
          setUser(readySession.user);
          setAuthError(false);
        } else {
          console.log('[Auth] No stored session found');
          setSession(null);
          setUser(null);
        }
      } catch (err) {
        console.error('[Auth] getSession unexpected error:', err);
        // Do not force authError on transient mobile/network startup failures.
        setAuthError(false);
      } finally {
        // Mark hydration complete AFTER getSession resolves (or times out)
        setLoading(false);
        setIsHydrated(true);
      }


      // NOW set up the listener for subsequent auth changes (sign-in, sign-out, token refresh)
      const { data } = auth.onAuthStateChange(
        (event, session) => {
          console.log('[Auth] State change:', event);

          // Skip INITIAL_SESSION — we already handled it above via getSession()
          if (event === 'INITIAL_SESSION') {
            return;
          }

          if (event === 'SIGNED_OUT') {
            setSession(null);
            setUser(null);
            setLoading(false);
            setIsHydrated(true);
            setAuthError(false);
            sessionStorage.removeItem('quiet_draft_nudge_session');
            sessionStorage.removeItem('quiet_draft_nudge_dismissed');
            return;
          }

          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            // If signIn() already populated valid state, skip re-validation
            if (signInJustCompleted.current) {
              console.log('[Auth] Skipping re-validation, signIn already completed');
              signInJustCompleted.current = false;
              return;
            }
            if (session) {
              setSession(session);
              setUser(session.user);
              setAuthError(false);
              setLoading(false);
              setIsHydrated(true);
            }
            return;
          }

          // Fallback for other events (USER_UPDATED, etc.)
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          setIsHydrated(true);
        }
      );
      subscription = data.subscription;
    };

    initAuth();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string, referralCode?: string) => {
    try {
      const { error } = await auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            full_name: fullName,
            ...(referralCode ? { referral_code: referralCode } : {}),
          },
        },
      });
      return { error: error as Error | null };
    } catch (err) {
      console.error('[Auth] signUp error:', err);
      return { error: err as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setAuthError(false);
      setLoading(true);
      const { data, error } = await auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setLoading(false);
        return { error: error as Error | null };
      }
      // Immediately populate auth state from response to avoid hydration race
      if (data.session && data.user) {
        console.log('[Auth] signIn success, setting state immediately');
        signInJustCompleted.current = true;
        setSession(data.session);
        setUser(data.user);
        setAuthError(false);
        setIsHydrated(true);
        setLoading(false);
      }
      return { error: null };
    } catch (err) {
      console.error('[Auth] signIn error:', err);
      setLoading(false);
      return { error: err as Error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      setAuthError(false);
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      return { error: error as Error | null };
    } catch (err) {
      console.error('[Auth] Google signIn error:', err);
      return { error: err as Error };
    }
  };

  const signInWithApple = async () => {
    try {
      setAuthError(false);
      const { error } = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });
      return { error: error as Error | null };
    } catch (err) {
      console.error('[Auth] Apple signIn error:', err);
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    try {
      await auth.signOut();
    } catch (err) {
      console.error('[Auth] signOut error:', err);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error: error as Error | null };
    } catch (err) {
      console.error('[Auth] resetPassword error:', err);
      return { error: err as Error };
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await auth.updateUser({ password });
      return { error: error as Error | null };
    } catch (err) {
      console.error('[Auth] updatePassword error:', err);
      return { error: err as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isHydrated,
        authError,
        signUp,
        signIn,
        signInWithGoogle,
        signInWithApple,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
